import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";
import { MessageCircle, Send, ArrowLeft, Search } from "lucide-react";

function relTime(iso) {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ""; }
}

function initial(name) {
  return (name || "?").trim().charAt(0).toUpperCase();
}

export default function Messages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);
  const listEnd = useRef(null);

  useEffect(() => { document.title = "Messages · Jirani"; }, []);

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/dm/threads");
      setThreads(data.threads || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingList(false); }
  };

  useEffect(() => {
    if (!user) return;
    loadThreads();
    const t = setInterval(loadThreads, 20000);
    return () => clearInterval(t);
  }, [user]);

  const openThread = async (thread) => {
    setActive(thread);
    setLoadingMsgs(true);
    setMessages([]);
    try {
      const { data } = await api.get(`/dm/threads/${thread.id}/messages`);
      setMessages(data.messages || []);
      // mark local unread as 0 after load
      setThreads((ts) => ts.map((t) => (t.id === thread.id ? { ...t, unread: 0 } : t)));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingMsgs(false); }
  };

  // Deep-link support: /messages?start=<peer_id>
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(location.search);
    const startPeer = params.get("start");
    if (!startPeer) return;
    (async () => {
      try {
        const { data } = await api.post("/dm/threads", { peer_id: startPeer });
        // clear query so refresh doesn't restart
        navigate("/messages", { replace: true });
        await loadThreads();
        openThread(data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, user]);

  useEffect(() => {
    listEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || !active || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/dm/threads/${active.id}/messages`, { content: text });
      setMessages((ms) => [...ms, data]);
      setDraft("");
      // refresh thread order in list
      setThreads((ts) =>
        [
          { ...(ts.find((t) => t.id === active.id) || active), last_message: text.slice(0, 60), last_message_at: data.created_at },
          ...ts.filter((t) => t.id !== active.id),
        ]
      );
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const filtered = threads.filter((t) =>
    !q.trim() || (t.peer?.name || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="messages-page" className="mx-auto max-w-6xl px-0 py-6 sm:px-4 lg:px-8">
      <div className="grid overflow-hidden rounded-none border-y border-black/5 bg-white sm:rounded-3xl sm:border md:grid-cols-[320px_1fr]">
        {/* LEFT: Threads list */}
        <aside
          data-testid="dm-threads"
          className={`${active ? "hidden md:block" : ""} border-r border-black/5`}
        >
          <div className="border-b border-black/5 p-4">
            <div className="jirani-eyebrow">Direct messages</div>
            <h2
              className="mt-2 flex items-center gap-2 text-2xl text-[#1A1A1A]"
              style={{ fontFamily: "Outfit", fontWeight: 700 }}
            >
              <MessageCircle className="h-6 w-6 text-[#D95D39]" /> Inbox
            </h2>
            <div className="relative mt-3">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2E473B]/60" />
              <Input
                data-testid="dm-search"
                placeholder="Search neighbors"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-10 rounded-full border-black/10 bg-[#F9F7F4] pl-9"
              />
            </div>
          </div>
          <ul className="max-h-[70vh] overflow-y-auto">
            {loadingList ? (
              <li className="p-6 text-sm text-[#4A4A4A]">Loading conversations…</li>
            ) : filtered.length === 0 ? (
              <li className="p-6 text-center text-sm text-[#4A4A4A]">
                No messages yet. Tap “Message” on any post, listing, or event to start one.
              </li>
            ) : (
              filtered.map((t) => (
                <li key={t.id}>
                  <button
                    data-testid={`dm-thread-${t.id}`}
                    onClick={() => openThread(t)}
                    className={`flex w-full items-start gap-3 border-b border-black/5 p-4 text-left transition-colors hover:bg-[#F9F7F4] ${
                      active?.id === t.id ? "bg-[#F4A261]/10" : ""
                    }`}
                  >
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#2E473B] text-sm font-semibold text-white">
                      {initial(t.peer?.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="truncate text-sm font-semibold text-[#1A1A1A]">
                          {t.peer?.name || "A neighbor"}
                        </div>
                        {t.last_message_at && (
                          <div className="shrink-0 text-[10px] text-[#4A4A4A]">
                            {relTime(t.last_message_at)}
                          </div>
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2">
                        <div className="truncate text-xs text-[#4A4A4A]">
                          {t.last_message || "Say habari!"}
                        </div>
                        {t.unread > 0 && (
                          <span className="ml-auto shrink-0 rounded-full bg-[#D95D39] px-2 py-0.5 text-[10px] font-bold text-white">
                            {t.unread}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))
            )}
          </ul>
        </aside>

        {/* RIGHT: Thread view */}
        <section
          data-testid="dm-thread-view"
          className={`${active ? "block" : "hidden md:block"} flex min-h-[70vh] flex-col`}
        >
          {!active ? (
            <div className="grid flex-1 place-items-center p-10 text-center text-[#4A4A4A]">
              Pick a conversation to see messages, or start a new one from a neighbor's post.
            </div>
          ) : (
            <>
              <header className="flex items-center gap-3 border-b border-black/5 p-4">
                <button
                  data-testid="dm-back"
                  onClick={() => setActive(null)}
                  className="grid h-9 w-9 place-items-center rounded-full border border-black/10 bg-white md:hidden"
                  aria-label="Back to inbox"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <div className="grid h-10 w-10 place-items-center rounded-full bg-[#2E473B] text-sm font-semibold text-white">
                  {initial(active.peer?.name)}
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#1A1A1A]">
                    {active.peer?.name || "A neighbor"}
                  </div>
                  {active.peer?.neighborhood && (
                    <div className="text-xs text-[#2E473B]/70">{active.peer.neighborhood}</div>
                  )}
                </div>
              </header>

              <div
                data-testid="dm-messages"
                className="flex-1 space-y-3 overflow-y-auto bg-[#F9F7F4] p-4"
              >
                {loadingMsgs ? (
                  <div className="text-center text-sm text-[#4A4A4A]">Loading…</div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-sm text-[#4A4A4A]">
                    Say habari to break the ice.
                  </div>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_id === user?.user_id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div
                          data-testid={`dm-msg-${m.id}`}
                          className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                            mine
                              ? "rounded-br-md bg-[#D95D39] text-white"
                              : "rounded-bl-md border border-black/5 bg-white text-[#1A1A1A]"
                          }`}
                        >
                          <div className="whitespace-pre-wrap">{m.content}</div>
                          <div
                            className={`mt-1 text-right text-[10px] ${
                              mine ? "text-white/80" : "text-[#2E473B]/60"
                            }`}
                          >
                            {(() => { try { return format(new Date(m.created_at), "h:mm a"); } catch { return ""; } })()}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={listEnd} />
              </div>

              <form onSubmit={send} className="flex items-center gap-2 border-t border-black/5 p-3">
                <Input
                  data-testid="dm-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={`Message ${active.peer?.name?.split(" ")[0] || "your neighbor"}...`}
                  className="h-11 flex-1 rounded-full border-black/10 bg-white"
                />
                <Button
                  data-testid="dm-send"
                  type="submit"
                  disabled={!draft.trim() || sending}
                  className="jirani-pill h-11 rounded-full bg-[#D95D39] px-5 text-white hover:bg-[#c25333]"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
